// Channel data source for loading and caching audio buffers

import type { AudioTrackLength } from '../managers/worklet-factory.js';
import { globalBufferPool } from '../memory/buffer-pool.js';
import { channelDataPool } from '../memory/channel-data-pool.js';

export interface ChannelDataSourceOptions {
  audioContext: AudioContext;
  url: string;
  trackLength: AudioTrackLength;
}

export class ChannelDataSource {
  private audioContext: AudioContext;
  private url: string;
  private trackLength: AudioTrackLength;
  private decodedAudioBuffer: AudioBuffer | null = null;
  private isLoading = false;
  private loadPromise: Promise<AudioBuffer> | null = null;

  constructor(audioContext: AudioContext, url: string, trackLength: AudioTrackLength) {
    this.audioContext = audioContext;
    this.url = url;
    this.trackLength = trackLength;
  }

  /**
   * Load and decode the audio buffer
   * @returns Promise resolving to the decoded AudioBuffer
   */
  async loadAudioBuffer(): Promise<AudioBuffer> {
    // Return cached buffer if available
    if (this.decodedAudioBuffer) {
      return this.decodedAudioBuffer;
    }

    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.isLoading = true;
    this.loadPromise = this.performLoad();

    try {
      const buffer = await this.loadPromise;
      this.decodedAudioBuffer = buffer;
      return buffer;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Perform the actual loading and decoding
   */
  private async performLoad(): Promise<AudioBuffer> {
    console.log('📥 Fetching audio file:', this.url);
    const response = await fetch(this.url);

    if (!response.ok) {
      throw new Error(`Failed to fetch audio file: ${response.status} ${response.statusText}`);
    }

    // Use buffer pool for response reading
    const contentLength = parseInt(response.headers.get('content-length') || '0', 10);
    const pooledBuffer = globalBufferPool.checkout(contentLength || 10 * 1024 * 1024); // 10MB default

    try {
      // Read response into pooled buffer
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      let totalBytesRead = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        if (value) {
          pooledBuffer.set(value, totalBytesRead);
          totalBytesRead += value.length;
        }
      }

      // Create ArrayBuffer from the portion we actually used
      const arrayBuffer = pooledBuffer.buffer.slice(0, totalBytesRead);

      console.log('🎵 Decoding audio data...');
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      console.log('✅ Audio decoded:', {
        duration: audioBuffer.duration,
        length: audioBuffer.length,
        sampleRate: audioBuffer.sampleRate,
        numberOfChannels: audioBuffer.numberOfChannels
      });

      return audioBuffer;
    } finally {
      // Always return buffer to pool
      globalBufferPool.checkin(pooledBuffer);
    }
  }

  /**
   * Read channel data from the buffer
   * @param start Start sample index
   * @param end End sample index
   * @returns Array of Float32Arrays, one per channel
   */
  async readChannelData(start: number, end: number): Promise<Float32Array[]> {
    const buffer = await this.loadAudioBuffer();

    const length = Math.min(end - start, buffer.length - start);
    if (length <= 0) {
      return [];
    }

    // Use channel data pool for allocations
    const numChunks = 1; // We're reading one chunk at a time
    const channelArrays = channelDataPool.checkout(numChunks, buffer.numberOfChannels);

    // We get back an array of chunks, use the first chunk
    const chunk = channelArrays[0];

    // Copy data into pooled arrays
    for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
      // Ensure the pooled array is the right size
      if (chunk[ch].length >= length) {
        buffer.copyFromChannel(chunk[ch], ch, start);
        // Return a view of the exact size needed
        if (chunk[ch].length > length) {
          chunk[ch] = chunk[ch].subarray(0, length);
        }
      } else {
        // Pooled array too small, allocate new one
        const newArray = new Float32Array(length);
        buffer.copyFromChannel(newArray, ch, start);
        chunk[ch] = newArray;
      }
    }

    // Return just the arrays, not the wrapper
    return chunk;
  }

  /**
   * Get track length for this source
   */
  getTrackLength(): AudioTrackLength {
    return this.trackLength;
  }

  /**
   * Get metadata for this source (legacy compatibility)
   */
  getMetadata(): AudioTrackLength {
    return this.trackLength;
  }

  /**
   * Get the URL for this source
   */
  getUrl(): string {
    return this.url;
  }

  /**
   * Check if the audio is currently loading
   */
  getIsLoading(): boolean {
    return this.isLoading;
  }

  /**
   * Check if the audio has been loaded
   */
  getIsLoaded(): boolean {
    return this.decodedAudioBuffer !== null;
  }

  /**
   * Get the loaded audio buffer (if available)
   */
  getAudioBuffer(): AudioBuffer | null {
    return this.decodedAudioBuffer;
  }

  /**
   * Get the buffer length for reading operations
   * Uses playbackLength for buffer management
   * @returns Number of samples available for reading
   */
  getBufferLength(): number {
    return this.decodedAudioBuffer?.length || this.trackLength.playbackLength;
  }

  /**
   * Get the decoded audio buffer length in samples (legacy compatibility)
   * This is the actual usable audio content after decodeAudioData() has removed encoder delay
   * @returns Number of samples in the decoded buffer, or 0 if not loaded
   */
  getDecodedLength(): number {
    return this.decodedAudioBuffer?.length || 0;
  }
}

/**
 * Factory function for creating channel data sources
 */
export function createChannelDataSource(
  audioContext: AudioContext,
  url: string,
  trackLength: AudioTrackLength
): ChannelDataSource {
  return new ChannelDataSource(audioContext, url, trackLength);
}
